/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import styles from "./reportPanel.module.css";

const cx = (...parts) => parts.filter(Boolean).join(" ");
const dash = (v) => (v === undefined || v === null || v === "" ? "—" : v);

function getAuthToken() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}")?.token;
  } catch {
    return null;
  }
}

function authHeader() {
  const token = getAuthToken();
  if (token) axios.defaults.headers.common["Authorization"] = token;
}

// Best-guess severity when the API doesn't send one explicitly.
// If your endpoint returns `latestFlagSeverity` ("stop" | "warn"), that wins.
function severityOf(p) {
  if (p.latestFlagSeverity) return p.latestFlagSeverity;
  if (!p.latestFlagType) return "";
  return /overdue|stop|exclu/i.test(p.latestFlagType) ? "stop" : "warn";
}

function painTrend(p) {
  const now = p.pain_level;
  const base = p.baselinePainLevel;
  if (now === undefined || now === null) return null;
  if (base === undefined || base === null) return { dir: null, label: `${now}` };
  return { dir: now > base ? "up" : now < base ? "down" : "flat", label: `${base} → ${now}` };
}

const FILTERS = [
  { key: "exc", label: "Exceptions first" },
  { key: "all", label: "Everyone" },
  { key: "measure", label: "Measure due" },
  { key: "paused", label: "Paused" },
];

const STATES = [
  { value: "", label: "All states" },
  { value: "TX", label: "Texas" },
  { value: "GA", label: "Georgia" },
  { value: "NC", label: "North Carolina" },
  { value: "AZ", label: "Arizona" },
];

// ── Detail (expanded row) ────────────────────────────────────────────────
// `d` is whatever comes back from GET .../beneficiary/:ref. All fields are
// optional so the card layout still renders, empty, if the API returns
// nothing or a partial record.
function Detail({ p, d }) {
  const sev = severityOf(p);
  // These map to PanelReportModel.getBeneficiaryDetail():
  //   alignment       -> raw access_beneficiary_state row (SELECT *)
  //   condition       -> single self_user_info row (cpg_id/cpg_label/cpg_value/
  //                       final_cpg_classification/final_pain_subtype/pain_level)
  //   outcomeMeasures -> ARRAY of questionnaire_targets rows (one per instrument)
  //   timeline        -> merged events + outreach rows, newest first
  //   careTeam        -> single care_team_clinicians row, or null
  const alignment = d?.alignment || {};
  const condition = d?.condition || {};
  const outcomeRows = d?.outcomeMeasures || [];
  const timeline = d?.timeline || [];
  const careTeam = d?.careTeam || null;

  return (
    <>
      {sev === "stop" && (
        <div className={cx(styles.excbar, styles.excbarStop)}>
          <b>{dash(p.latestFlagType)}</b>
          Care is stopped and does not restart without a recorded decision from
          you. The beneficiary has been told you are looking at it.
        </div>
      )}
      {sev === "warn" && (
        <div className={styles.excbar}>
          <b>{dash(p.latestFlagType)}</b>
          Care continues. This is for your attention rather than your
          permission.
        </div>
      )}

      <div className={styles.dgrid}>
        <div className={styles.dcard}>
          <h5>Alignment and state</h5>
          <div className={styles.dl}>
            <div>Licensure state</div>
            <div>{dash(p.state)}</div>
            <div>Alignment date</div>
            <div>{dash(alignment.alignment_date)}</div>
            <div>Day of period</div>
            <div>{p.dayOfPeriod != null ? `${p.dayOfPeriod} of 365` : "—"}</div>
            <div>Reporting window opens</div>
            <div>{dash(alignment.next_reporting_window_open ?? p.next_reporting_window_open)}</div>
            <div>Reporting window closes</div>
            <div>{dash(alignment.next_reporting_window_close)}</div>
          </div>
        </div>

        <div className={styles.dcard}>
          <h5>Condition and classification</h5>
          <div className={styles.dl}>
            <div>Guideline</div>
            <div>{dash(condition.cpg_label ?? condition.final_cpg_classification)}</div>
            <div>Classification</div>
            <div>{dash(condition.cpg_value ?? condition.cpg_id)}</div>
            <div>Pain subtype</div>
            <div>{dash(condition.final_pain_subtype)}</div>
          </div>
        </div>

        <div className={styles.dcard}>
          <h5>Outcome measures</h5>
          <div className={styles.dl}>
            <div>Next due</div>
            <div>{dash(p.next_reporting_window_open)}</div>
            <div>Pain now</div>
            <div>{p.pain_level != null ? `${p.pain_level} of 10` : "—"}</div>
          </div>
          {outcomeRows.length ? (
            outcomeRows.map((o, i) => (
              <div
                key={i}
                className={styles.dl}
                style={{ marginTop: i === 0 ? ".6rem" : ".3rem", borderTop: "1px solid var(--gray100, #eee)", paddingTop: ".4rem" }}
              >
                <div>Instrument</div>
                <div>{dash(o.instrument_label)}</div>
                <div>Baseline</div>
                <div>{dash(o.baseline_value)}</div>
                <div>Target</div>
                <div>{dash(o.target_value)}</div>
                <div>Scale</div>
                <div>
                  {o.scale_min != null && o.scale_max != null ? `${o.scale_min}–${o.scale_max}` : "—"}
                </div>
              </div>
            ))
          ) : (
            <div style={{ marginTop: ".6rem", fontSize: ".9rem", color: "var(--gray500)" }}>
              No outcome instruments on record.
            </div>
          )}
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Recent record</h5>
          <div className={styles.timeline}>
            {timeline.length ? (
              timeline.map((t, i) => (
                <div className={styles.tl} key={i}>
                  <div className={styles.d}>{dash(t.created_at)}</div>
                  <div>
                    {dash(t.event_type)}
                    {t.action_taken ? ` — ${t.action_taken}` : ""}
                    {t.actor ? ` (${t.actor})` : ""}
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.tl}>
                <div className={styles.d}>—</div>
                <div>No recent activity on record.</div>
              </div>
            )}
          </div>
        </div>

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Care team</h5>
          <div style={{ fontSize: ".92rem" }}>
            {careTeam ? (
              <>
                {dash(careTeam.clinician_name ?? careTeam.name)}
                {careTeam.clinic_name ? `, ${careTeam.clinic_name}` : ""}
                {careTeam.status ? ` — ${careTeam.status}` : ""}
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
      </div>

      <div className={styles.acts}>
        {sev === "stop" ? (
          <>
            <button className={cx(styles.btn, styles.btnPrimary)}>
              Resume with a modified plan
            </button>
            <button className={styles.btn}>Hold and re-check</button>
            <button className={styles.btn}>
              Refer out for in-person assessment
            </button>
            <button className={cx(styles.btn, styles.btnDanger)}>
              Exclusion pathway, Section 4.7
            </button>
          </>
        ) : (
          <>
            <button className={cx(styles.btn, styles.btnPrimary)}>
              Acknowledge and close
            </button>
            <button className={styles.btn}>Add a note to the record</button>
            <button className={styles.btn}>Open the full history</button>
          </>
        )}
      </div>

      <div
        style={{ fontSize: ".85rem", color: "var(--gray500)", marginTop: ".8rem" }}
      >
        Every action here writes to the escalation record with your name,
        license and the reason. Nothing closes without one.
      </div>
    </>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────
function Row({ p, open, loadingDetail, detail, onToggle }) {
  const sev = severityOf(p);
  const trend = painTrend(p);
  const monthLabel = p.monthStatus || p.month || null;

  return (
    <>
      <div
        className={cx(styles.brow, sev && styles.browExc, open && styles.browOpen)}
        onClick={onToggle}
      >
        <div className={styles.caret}>▶</div>
        <div className={styles.name}>
          {dash(p.legalFirstName)} {dash(p.legalLastName)}
          <span>{dash(p.beneficiaryId)}</span>
        </div>
        <div>{dash(p.state)}</div>
        <div className={styles.hideSm}>{dash(p.dayOfPeriod)}</div>
        <div
          className={cx(
            styles.hideSm,
            trend?.dir && styles.trend,
            trend?.dir === "up" ? styles.trendUp : trend?.dir === "down" ? styles.trendDown : ""
          )}
        >
          {trend ? trend.label : "—"}
        </div>
        <div className={styles.hideSm}>{dash(p.next_reporting_window_open)}</div>
        <div>
          {p.latestFlagType ? (
            <span className={cx(styles.tag, sev === "stop" ? styles.tagStop : styles.tagWarn)}>
              {p.latestFlagType}
            </span>
          ) : (
            <span className={cx(styles.tag, styles.tagGrey)}>None</span>
          )}
        </div>
        <div className={styles.hideSm}>
          {monthLabel === "Ready" ? (
            <span className={styles.tag}>Ready</span>
          ) : monthLabel ? (
            <span className={cx(styles.tag, styles.tagStop)}>{monthLabel}</span>
          ) : (
            <span className={cx(styles.tag, styles.tagGrey)}>—</span>
          )}
        </div>
      </div>
      <div className={cx(styles.detail, open && styles.detailOn)}>
        {open &&
          (loadingDetail ? (
            <div className={styles.dcard}>Loading record…</div>
          ) : (
            <Detail p={p} d={detail} />
          ))}
      </div>
    </>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────
export default function ReportPanel() {
  const [view, setView] = useState("panel");
  const [filter, setFilter] = useState("exc");
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [openRows, setOpenRows] = useState(new Set());
  const [attested, setAttested] = useState(false);

  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [loadingDetailRef, setLoadingDetailRef] = useState(null);
  const [monthEnd, setMonthEnd] = useState(null);

  const [loadingPeople, setLoadingPeople] = useState(true);
  const [loadingMonthEnd, setLoadingMonthEnd] = useState(true);

  const [toast, setToast] = useState(null); // { type: "info" | "success" | "error", text }
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchSummary = useCallback(async () => {
    authHeader();
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/summary`);
      if (resp.data.success) setSummary(resp.data.data);
    } catch (e) {
      // summary stays null — the strip below falls back to "—" per stat
    }
  }, []);

  const fetchBeneficiaries = useCallback(async () => {
    authHeader();
    setLoadingPeople(true);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiaries`, {
        params: { filter, state: stateFilter, search },
      });
      if (resp.data.success) setPeople(resp.data.data || []);
      else setPeople([]);
    } catch (e) {
      setPeople([]);
    } finally {
      setLoadingPeople(false);
    }
  }, [filter, stateFilter, search]);

  const fetchMonthEnd = useCallback(async () => {
    authHeader();
    setLoadingMonthEnd(true);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/month-end`);
      if (resp.data.success) setMonthEnd(resp.data.data);
    } catch (e) {
      // monthEnd stays null — the tab still renders with empty defaults
    } finally {
      setLoadingMonthEnd(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchMonthEnd();
  }, [fetchSummary, fetchMonthEnd]);

  useEffect(() => {
    fetchBeneficiaries();
  }, [fetchBeneficiaries]);

  const toggleRow = async (ref) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });

    if (!detailCache[ref]) {
      authHeader();
      setLoadingDetailRef(ref);
      try {
        const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}`);
        if (resp.data.success) {
          setDetailCache((prev) => ({ ...prev, [ref]: resp.data.data }));
        }
      } catch (e) {
        // leave this ref out of the cache; Detail renders its empty state
      } finally {
        setLoadingDetailRef((cur) => (cur === ref ? null : cur));
      }
    }
  };

  const approve = async () => {
    if (!attested) {
      setToast({ type: "info", text: "Please tick the attestation before approving." });
      return;
    }
    if (readyCount <= 0) {
      setToast({ type: "info", text: "Nothing to approve — 0 claims are ready this month." });
      return;
    }
    authHeader();
    setApproving(true);
    try {
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/panel_report/month-end/approve`, {
        attestationText:
          "I have reviewed the care furnished to the beneficiaries on my panel and attest that active care delivery was furnished in ongoing compliance with the model requirements.",
      });
      if (resp.data.success) {
        setToast({ type: resp.data.approvedCount > 0 ? "success" : "info", text: resp.data.message });
        setAttested(false);
        fetchMonthEnd();
        fetchBeneficiaries();
      } else {
        setToast({ type: "error", text: resp.data.message || "Failed to approve batch." });
      }
    } catch (err) {
      setToast({ type: "error", text: "Failed to approve batch." });
    } finally {
      setApproving(false);
    }
  };

  const readyCount = monthEnd?.readyCount ?? 0;
  const blocked = monthEnd?.blocked ?? [];
  const period = monthEnd?.period ?? "This period";
  const totalBillable = monthEnd?.totalBillable ?? 0;
  const needsDecision = monthEnd?.needsDecision ?? blocked.length;
  const totalAmount = typeof monthEnd?.totalAmount === "number" ? monthEnd.totalAmount : 0;
  const cptAmount = monthEnd?.cptAmount ?? "—";
  const notBillable = monthEnd?.notBillable;

  return (
    <div className={styles.page}>
      <div className={styles.bar}>
        <div className={styles.barIn}>
          <div className={styles.logo}>
            <span className={styles.v}>virdio</span>
            <span className={styles.h}>health</span>
          </div>
          <div className={styles.who}>
            {dash(summary?.therapistName)} &nbsp;|&nbsp; Licensed{" "}
            {summary?.licensedStates?.length ? summary.licensedStates.join(", ") : "—"} &nbsp;|&nbsp; Panel
            of {summary?.onPanel ?? "—"}
          </div>
        </div>
      </div>

      <div className={styles.wrap}>
        <h1>Panel report</h1>
        <p className={styles.sub}>
          One report, two views. The panel carries every beneficiary with
          exceptions surfaced in place and expandable to the full record. The
          month-end review is the Section 4.03(E) attestation and the gate
          before claims go out.
        </p>

        <div className={styles.tabs}>
          <button
            className={cx(styles.tab, view === "panel" && styles.tabOn)}
            onClick={() => setView("panel")}
          >
            Panel
          </button>
          <button
            className={cx(styles.tab, view === "month" && styles.tabOn)}
            onClick={() => setView("month")}
          >
            Month-end review <span className={styles.cnt}>{needsDecision}</span>
          </button>
        </div>

        {/* ══════════════ PANEL ══════════════ */}
        <div className={cx(styles.view, view === "panel" && styles.viewOn)}>
          <div className={styles.strip}>
            <div className={styles.stat}>
              <div className={styles.n}>{summary?.onPanel ?? "—"}</div>
              <div className={styles.l}>On panel</div>
            </div>
            <div className={cx(styles.stat, styles.statAlert)}>
              <div className={styles.n}>{summary?.openExceptions ?? "—"}</div>
              <div className={styles.l}>Open exceptions</div>
            </div>
            <div className={cx(styles.stat, styles.statWarn)}>
              <div className={styles.n}>{summary?.measureDueIn14Days ?? "—"}</div>
              <div className={styles.l}>Measure due in 14 days</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.n}>{summary?.billableThisMonth ?? "—"}</div>
              <div className={styles.l}>Billable this month</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.n}>{summary?.paused ?? "—"}</div>
              <div className={styles.l}>Paused</div>
            </div>
          </div>

          <div className={styles.filters}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                className={cx(styles.chip, filter === f.key && styles.chipOn)}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              {STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <input
              type="search"
              placeholder="Search name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 180 }}
            />
          </div>

          <div className={styles.tbl}>
            <div className={styles.hrow}>
              <div></div>
              <div>Beneficiary</div>
              <div>State</div>
              <div className={styles.hideSm}>Day</div>
              <div className={styles.hideSm}>Pain</div>
              <div className={styles.hideSm}>Next measure</div>
              <div>Flag</div>
              <div className={styles.hideSm}>Month</div>
            </div>
            <div>
              {loadingPeople ? (
                <div className={styles.brow}>Loading panel…</div>
              ) : people.length ? (
                people.map((p) => (
                  <Row
                    key={p.beneficiaryId}
                    p={p}
                    open={openRows.has(p.beneficiaryId)}
                    loadingDetail={loadingDetailRef === p.beneficiaryId}
                    detail={detailCache[p.beneficiaryId]}
                    onToggle={() => toggleRow(p.beneficiaryId)}
                  />
                ))
              ) : (
                <div className={styles.brow}>No beneficiaries match this view.</div>
              )}
            </div>
          </div>

          <h2>What is on this view and why</h2>
          <div className={styles.good}>
            Combining the panel and the exception queue means the therapist
            works one list rather than switching between two. Exceptions sort
            to the top and stay in place in the panel, so the context around a
            flagged beneficiary is one row away rather than one screen away.
            Expanding a row opens the full record required at Section 6.1, so
            there is no third screen either.
          </div>
          <div className={styles.note}>
            <b>Alerts route by state of licensure.</b> This therapist sees only
            beneficiaries in states where she holds a license. That is not a
            filter she sets, it is how the panel is built.
          </div>
        </div>

        {/* ══════════════ MONTH END ══════════════ */}
        <div className={cx(styles.view, view === "month" && styles.viewOn)}>
          <div className={styles.mhead}>
            <div>
              <div className={styles.big}>{period} review</div>
              <div className={styles.m}>
                {totalBillable} beneficiaries with care furnished this month.{" "}
                {needsDecision} need a decision before the batch can be approved.
                {notBillable != null && ` ${notBillable} had no care furnished and are not billable.`}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className={styles.big} style={{ color: "var(--teal-deep)" }}>
                ${totalAmount.toFixed(2)}
              </div>
              <div className={styles.m}>
                {readyCount} claims at ${cptAmount} billed to Medicare
              </div>
            </div>
          </div>

          <div className={styles.note}>
            <b>Only the beneficiaries below need individual attention.</b> The
            rest have care furnished, no open exception, no pause days and a
            complete record. They are approved as a batch by the single action
            at the bottom, and the attestation is recorded against every one of
            them individually.
          </div>

          <div className={styles.tbl}>
            <div className={cx(styles.mrow, styles.mrowHead)}>
              <div>Beneficiary</div>
              <div>Date of service</div>
              <div>What furnished care</div>
              <div>Why it needs you</div>
              <div>Decision</div>
            </div>

            {loadingMonthEnd ? (
              <div className={styles.mrow}>Loading month-end review…</div>
            ) : blocked.length ? (
              blocked.map((b) => (
                <div key={b.id} className={cx(styles.mrow, styles.mrowBlocked)}>
                  <div>
                    <strong>
                      {dash(b.legalFirstName)} {dash(b.legalLastName)}
                    </strong>
                    <br />
                    <span style={{ fontSize: ".82rem", color: "var(--gray500)" }}>
                      {dash(b.patient_id)}
                    </span>
                  </div>
                  <div>{dash(b.date_of_service)}</div>
                  <div>—</div>
                  <div style={{ color: "var(--red-text)", fontWeight: 600 }}>
                    {dash(b.blockingReason || "Needs review")}
                  </div>
                  <div>
                    <button className={cx(styles.btn, styles.btnPrimary)}>Open record</button>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.mrow}>Nothing is blocking this batch.</div>
            )}

            <div className={styles.mrow} style={{ background: "var(--gray50)" }}>
              <div>
                <strong>{readyCount} others</strong>
                <br />
                <span style={{ fontSize: ".82rem", color: "var(--gray500)" }}>
                  No open exception, no pause, complete record
                </span>
              </div>
              <div>Various</div>
              <div>Sessions and measures</div>
              <div style={{ color: "var(--gray500)" }}>Nothing outstanding</div>
              <div>
                <span className={styles.tag}>Ready</span>
              </div>
            </div>
          </div>

          <div className={styles.attest}>
            <label>
              <input
                type="checkbox"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
              />
              <span className={styles.t}>
                I have reviewed the care furnished to the beneficiaries on my
                panel for {period} and I attest that active care delivery was
                furnished and that care was delivered in ongoing compliance
                with the model requirements.
                <span
                  style={{
                    display: "block",
                    marginTop: ".5rem",
                    fontWeight: 400,
                    fontSize: ".9rem",
                    color: "var(--gray600)",
                  }}
                >
                  Recorded against each of the {readyCount} beneficiaries in
                  this batch, with my name, license and the timestamp. This is
                  the Section 4.03(E) attestation. There is no separate CMS
                  form. Approving releases the claims.
                </span>
              </span>
            </label>
            <div className={styles.acts}>
              <button
                className={cx(styles.btn, styles.btnPrimary)}
                onClick={approve}
                disabled={approving || readyCount <= 0}
                style={readyCount <= 0 ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              >
                {approving ? "Approving…" : `Approve ${readyCount} and release claims`}
              </button>
              <button className={styles.btn}>Export the list</button>
            </div>
          </div>

          <h2>How the attestation is made, and how much work it is</h2>
          <div className={styles.good}>
            <b>CMS requires no per-beneficiary sign-off.</b> Section 4.03(E)
            makes the attestation of active care delivery and ongoing
            compliance through claim submission itself. There is no form, no
            signature per patient and no named reviewer per patient. The
            monthly review is our internal control, so we get to shape it.
          </div>
          <div className={styles.note}>
            <b>So the volume is one action, not {totalBillable || "many"}.</b>{" "}
            Beneficiaries with a complete record and no open exception clear as
            a batch. The only ones pulled out are those where something is
            genuinely unresolved: an open exception, a mid-month pause, or a
            month whose only qualifying event is ambiguous. At panel scale
            that is a handful, not a panel.
          </div>
          <div className={styles.note}>
            <b>What blocks a claim.</b> No qualifying event in the calendar
            month. An open level B exception with no recorded decision. A
            pause covering the whole month. Everything else releases.
          </div>
        </div>
      </div>
      {toast && (
        <div
          role="status"
          style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            padding: "12px 20px", borderRadius: 8, color: "#fff", fontSize: ".92rem",
            zIndex: 1000, boxShadow: "0 4px 16px rgba(0,0,0,.2)",
            background: toast.type === "success" ? "#1a7f4e" : toast.type === "error" ? "#c62828" : "#333",
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
